import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import API_URL from '../api';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
    Eye,
    CheckCircle,
    TrendingUp,
    Link2,
    BarChart3,
    Percent,
    Layers,
    Users,
    AlertTriangle,
    Download,
    MapPin
} from 'lucide-react';
import './Analytics.css';

const TYPE_LABELS = {
    desk: 'Сервірування',
    'multi-desk': 'Мульти-стіл',
    game: 'Гра',
    quiz: 'Квіз',
    complex: 'Комплексний',
    pizza: 'Конструктор піци'
};

const PIE_COLORS = ['#38bdf8', '#6366f1', '#4ade80', '#fbbf24', '#f87171'];

const Analytics = ({ user }) => {
    const [days, setDays] = useState(30);
    const [overview, setOverview] = useState(null);
    const [traffic, setTraffic] = useState(null);
    const [summary, setSummary] = useState(null);
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('all');
    const [selectedCity, setSelectedCity] = useState('');
    const [citiesList, setCitiesList] = useState([]);
    const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'dark');
    const [sortConfig, setSortConfig] = useState({ key: 'avgPercentage', direction: 'desc' });

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark';
            setTheme(currentTheme);
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);

    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    // Load cities list once on mount from the public /api/cities endpoint
    useEffect(() => {
        const platform = user?.platform || '';
        const url = platform
            ? `${API_URL}/cities?platform=${encodeURIComponent(platform)}`
            : `${API_URL}/cities`;
        axios.get(url)
            .then(res => {
                const names = (res.data || []).map(c => c.name || c).filter(Boolean);
                setCitiesList(names);
            })
            .catch(err => console.error('Failed to load cities list:', err));
    }, [user?.platform]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const cityQuery = selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : '';
            const summaryQuery = `days=${days}${typeFilter !== 'all' ? `&type=${typeFilter}` : ''}${cityQuery}`;
            
            const [ovRes, trRes, smRes, tsRes] = await Promise.all([
                axios.get(`${API_URL}/analytics/overview?city=${encodeURIComponent(selectedCity)}`, config),
                axios.get(`${API_URL}/analytics/traffic?days=${days}${cityQuery}`, config),
                axios.get(`${API_URL}/analytics/summary?${summaryQuery}`, config),
                axios.get(`${API_URL}/analytics/tests?days=${days}${cityQuery}`, config)
            ]);
            
            setOverview(ovRes.data);
            setTraffic(trRes.data);
            setSummary(smRes.data);
            setTests(tsRes.data);
        } catch (err) {
            console.error('Analytics fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [days, typeFilter, selectedCity]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const handleExportCSV = () => {
        let csvContent = "\uFEFF"; // BOM for Excel Cyrillic support
        
        // Section: Overview
        csvContent += "=== ЗАГАЛЬНИЙ ОГЛЯД ===\r\n";
        csvContent += "Показник;Значення\r\n";
        csvContent += `Всього відвідувань;${overview?.views?.total ?? 0}\r\n`;
        csvContent += `Відвідувань сьогодні;${overview?.views?.today ?? 0}\r\n`;
        csvContent += `Завершень сьогодні;${overview?.completions?.today ?? 0}\r\n`;
        csvContent += `Успішність;${summary?.overview?.passRate ?? 0}%\r\n`;
        csvContent += `Середній бал;${summary?.overview?.avgPercentage ?? 0}%\r\n`;
        csvContent += `Активних працівників;${summary?.overview?.activeStudents ?? 0}\r\n\r\n`;
        
        // Section: City Stats
        csvContent += "=== СТАТИСТИКА ПО МІСТАХ ===\r\n";
        csvContent += "Місто;Активних працівників;Проходжень;Середній бал;Успішність;Слабке місце\r\n";
        const cityRows = summary?.byCity || [];
        cityRows.forEach(row => {
            csvContent += `"${row.city}";${row.activeStudents};${row.count};${row.avgPercentage}%;${row.passRate}%;"${TYPE_LABELS[row.weakestType] || '—'}"\r\n`;
        });
        csvContent += "\r\n";
        
        // Section: Risk Students
        csvContent += "=== ПРАЦІВНИКИ, ЯКІ ПОТРЕБУЮТЬ УВАГИ ===\r\n";
        csvContent += "Працівник;Місто;Посада;Проходжень;Не пройдено;Середній бал\r\n";
        const riskRows = summary?.riskStudents || [];
        riskRows.forEach(row => {
            const fullName = `${row.studentLastName || ''} ${row.studentName || ''}`.trim();
            csvContent += `"${fullName}";"${row.city || '—'}";"${row.position || '—'}";${row.count};${row.failed};${row.avgPercentage}%\r\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `serviq_analytics_${days}_days${selectedCity ? `_${selectedCity}` : ''}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const typeChartData = traffic?.byType?.map(t => ({
        name: TYPE_LABELS[t._id] || t._id,
        Відвідувань: t.count
    })) || [];

    const cityPieData = traffic?.byCity?.map(c => ({
        name: c.city || 'Невідоме',
        value: c.count
    })) || [];

    const qualityOverview = summary?.overview || {};
    const cityRowsRaw = summary?.byCity || [];
    const typeRows = summary?.byType || [];
    const riskRows = summary?.riskStudents || [];

    const cityRows = useMemo(() => {
        const rows = [...cityRowsRaw];
        if (!sortConfig.key) return rows;
        rows.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            
            if (typeof aVal === 'string') {
                return sortConfig.direction === 'asc' 
                    ? aVal.localeCompare(bVal) 
                    : bVal.localeCompare(aVal);
            } else {
                return sortConfig.direction === 'asc' 
                    ? (aVal || 0) - (bVal || 0) 
                    : (bVal || 0) - (aVal || 0);
            }
        });
        return rows;
    }, [cityRowsRaw, sortConfig]);

    const requestSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    if (loading) {
        return (
            <div className="analytics-page">
                <div className="an-loading">Завантаження аналітики...</div>
            </div>
        );
    }

    return (
        <div className="analytics-page">
            <div className="analytics-header">
                <div>
                    <h1>📊 Аналітика</h1>
                    <p>Трафік та активність по тестах</p>
                </div>
                <div className="analytics-controls">
                    <button 
                        className="analytics-btn export-btn" 
                        onClick={handleExportCSV}
                        title="Завантажити звіт у форматі CSV"
                    >
                        <Download size={16} />
                        <span>Експорт звіту</span>
                    </button>
                    <div className="analytics-select-wrap">
                        <MapPin size={16} className="select-icon" />
                        <select
                            className="analytics-select with-icon"
                            value={selectedCity}
                            onChange={e => setSelectedCity(e.target.value)}
                        >
                            <option value="">Всі міста</option>
                            {citiesList.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                    </div>
                    <select
                        className="analytics-select"
                        value={days}
                        onChange={e => setDays(Number(e.target.value))}
                    >
                        <option value={7}>7 днів</option>
                        <option value={14}>14 днів</option>
                        <option value={30}>30 днів</option>
                        <option value={90}>90 днів</option>
                    </select>
                    <select
                        className="analytics-select"
                        value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}
                    >
                        <option value="all">Всі типи</option>
                        <option value="desk">Сервірування</option>
                        <option value="multi-desk">Мульти-стіл</option>
                        <option value="game">Гра</option>
                        <option value="quiz">Квіз</option>
                        <option value="complex">Комплексний</option>
                        <option value="pizza">Конструктор піци</option>
                    </select>
                </div>
            </div>

            {/* Карточки */}
            <div className="analytics-cards">
                <div className="an-card highlight">
                    <div className="an-card-icon-wrapper blue">
                        <Eye size={20} />
                    </div>
                    <div className="an-card-value">{overview?.views?.today ?? 0}</div>
                    <div className="an-card-label">Відвідувань сьогодні</div>
                    <div className="an-card-sub">За місяць: {overview?.views?.month ?? 0}</div>
                </div>
                <div className="an-card">
                    <div className="an-card-icon-wrapper green">
                        <CheckCircle size={20} />
                    </div>
                    <div className="an-card-value">{overview?.completions?.today ?? 0}</div>
                    <div className="an-card-label">Завершень сьогодні</div>
                    <div className="an-card-sub">За місяць: {overview?.completions?.month ?? 0}</div>
                </div>
                <div className="an-card">
                    <div className="an-card-icon-wrapper purple">
                        <TrendingUp size={20} />
                    </div>
                    <div className="an-card-value">{overview?.conversion?.today ?? 0}%</div>
                    <div className="an-card-label">Конверсія (сьогодні)</div>
                    <div className="an-card-sub">За місяць: {overview?.conversion?.month ?? 0}%</div>
                </div>
                <div className="an-card">
                    <div className="an-card-icon-wrapper amber">
                        <Link2 size={20} />
                    </div>
                    <div className="an-card-value">{overview?.activeTests ?? 0}</div>
                    <div className="an-card-label">Активних посилань</div>
                    <div className="an-card-sub">Ще не пройдено</div>
                </div>
                <div className="an-card">
                    <div className="an-card-icon-wrapper indigo">
                        <BarChart3 size={20} />
                    </div>
                    <div className="an-card-value">{overview?.views?.total ?? 0}</div>
                    <div className="an-card-label">Всього відвідувань</div>
                    <div className="an-card-sub">За весь час</div>
                </div>
            </div>

            <div className="analytics-section-title">
                <h2>Якість проходження</h2>
                <p>Результати за обраний період</p>
            </div>

            <div className="analytics-cards quality">
                <div className="an-card highlight">
                    <div className="an-card-icon-wrapper blue">
                        <Percent size={20} />
                    </div>
                    <div className="an-card-value">{qualityOverview.passRate ?? 0}%</div>
                    <div className="an-card-label">Успішність</div>
                    <div className="an-card-sub">Середній бал: {qualityOverview.avgPercentage ?? 0}%</div>
                </div>
                <div className="an-card">
                    <div className="an-card-icon-wrapper indigo">
                        <Layers size={20} />
                    </div>
                    <div className="an-card-value">{qualityOverview.total ?? 0}</div>
                    <div className="an-card-label">Проходжень</div>
                    <div className="an-card-sub">Не пройдено: {qualityOverview.failed ?? 0}</div>
                </div>
                <div className="an-card">
                    <div className="an-card-icon-wrapper green">
                        <Users size={20} />
                    </div>
                    <div className="an-card-value">{qualityOverview.activeStudents ?? 0}</div>
                    <div className="an-card-label">Активних працівників</div>
                    <div className="an-card-sub">За {days} днів</div>
                </div>
                <div className="an-card">
                    <div className="an-card-icon-wrapper red">
                        <AlertTriangle size={20} />
                    </div>
                    <div className="an-card-value">{riskRows.length}</div>
                    <div className="an-card-label">Потребують уваги</div>
                    <div className="an-card-sub">
                        Слабке місце: {TYPE_LABELS[qualityOverview.weakestType] || '—'}
                    </div>
                </div>
            </div>

            {/* Графіки */}
            <div className="analytics-charts">
                {/* Основний графік трафіку */}
                <div className="an-chart-card">
                    <p className="an-chart-title">Трафік за {days} днів</p>
                    {traffic?.chartData?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={traffic.chartData}>
                                <defs>
                                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorComp" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4ade80" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} />
                                <XAxis
                                    dataKey="date"
                                    stroke={theme === 'dark' ? '#94a3b8' : '#475569'}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={d => d.slice(5)}
                                />
                                <YAxis stroke={theme === 'dark' ? '#94a3b8' : '#475569'} tick={{ fontSize: 11 }} />
                                <Tooltip
                                    contentStyle={{
                                        background: theme === 'dark' ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                                        borderRadius: 12,
                                        color: theme === 'dark' ? '#f8fafc' : '#0f172a',
                                        backdropFilter: 'blur(10px)',
                                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                                    }}
                                    labelStyle={{ color: theme === 'dark' ? '#94a3b8' : '#475569', fontWeight: 'bold' }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Area
                                    type="monotone"
                                    dataKey="views"
                                    name="Відвідувань"
                                    stroke="#38bdf8"
                                    fill="url(#colorViews)"
                                    strokeWidth={2}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="completions"
                                    name="Завершень"
                                    stroke="#4ade80"
                                    fill="url(#colorComp)"
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="an-empty">
                            <span className="an-empty-icon">📭</span>
                            <span>Немає даних за цей період</span>
                        </div>
                    )}
                </div>

                {/* Розподіл по типах */}
                <div className="an-chart-card">
                    <p className="an-chart-title">По типах тестів</p>
                    {typeChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={typeChartData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} />
                                <XAxis type="number" stroke={theme === 'dark' ? '#94a3b8' : '#475569'} tick={{ fontSize: 11 }} />
                                <YAxis dataKey="name" type="category" width={90} stroke={theme === 'dark' ? '#94a3b8' : '#475569'} tick={{ fontSize: 11 }} />
                                <Tooltip
                                    contentStyle={{
                                        background: theme === 'dark' ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                                        borderRadius: 12,
                                        color: theme === 'dark' ? '#f8fafc' : '#0f172a',
                                        backdropFilter: 'blur(10px)',
                                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Bar dataKey="Відвідувань" fill="#6366f1" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="an-empty">
                            <span className="an-empty-icon">📭</span>
                            <span>Немає даних</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="analytics-insights">
                <div className="an-chart-card">
                    <p className="an-chart-title">Міста за результатами</p>
                    {cityRows.length > 0 ? (
                        <div className="an-table-wrap">
                            <table className="an-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => requestSort('city')} style={{ cursor: 'pointer', userSelect: 'none' }} className="sortable-th">
                                            Місто {sortConfig.key === 'city' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                                        </th>
                                        <th onClick={() => requestSort('activeStudents')} style={{ cursor: 'pointer', userSelect: 'none' }} className="sortable-th">
                                            Працівників {sortConfig.key === 'activeStudents' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                                        </th>
                                        <th onClick={() => requestSort('count')} style={{ cursor: 'pointer', userSelect: 'none' }} className="sortable-th">
                                            Проходжень {sortConfig.key === 'count' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                                        </th>
                                        <th onClick={() => requestSort('avgPercentage')} style={{ cursor: 'pointer', userSelect: 'none', color: 'var(--blue-light, #38bdf8)' }} className="sortable-th highlight-th">
                                            Середній бал {sortConfig.key === 'avgPercentage' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
                                        </th>
                                        <th onClick={() => requestSort('passRate')} style={{ cursor: 'pointer', userSelect: 'none' }} className="sortable-th">
                                            Успішність {sortConfig.key === 'passRate' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                                        </th>
                                        <th>Слабке місце</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cityRows.map((row) => (
                                        <tr key={row.city}>
                                            <td>{row.city}</td>
                                            <td>{row.activeStudents}</td>
                                            <td>{row.count}</td>
                                            <td>{row.avgPercentage}%</td>
                                            <td>
                                                <div className="conv-bar-wrap">
                                                    <div className="conv-bar-bg">
                                                        <div className="conv-bar-fill success" style={{ width: `${row.passRate}%` }} />
                                                    </div>
                                                    <span className="conv-pct">{row.passRate}%</span>
                                                </div>
                                            </td>
                                            <td>{TYPE_LABELS[row.weakestType] || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="an-empty">
                            <span className="an-empty-icon">📭</span>
                            <span>Немає результатів за цей період</span>
                        </div>
                    )}
                </div>

                <div className="an-chart-card">
                    <p className="an-chart-title">Типи тестів за успішністю</p>
                    {typeRows.length > 0 ? (
                        <div className="an-type-list">
                            {typeRows.map((row) => (
                                <div key={row.type} className="an-type-row">
                                    <div>
                                        <span className={`type-badge type-${row.type}`}>
                                            {TYPE_LABELS[row.type] || row.type}
                                        </span>
                                        <span className="an-type-count">{row.count} проходжень</span>
                                    </div>
                                    <div className="conv-bar-wrap">
                                        <div className="conv-bar-bg">
                                            <div className="conv-bar-fill success" style={{ width: `${row.passRate}%` }} />
                                        </div>
                                        <span className="conv-pct">{row.passRate}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="an-empty">
                            <span className="an-empty-icon">📭</span>
                            <span>Немає даних</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="an-chart-card an-risk-card">
                <p className="an-chart-title">Працівники, які потребують уваги</p>
                {riskRows.length > 0 ? (
                    <div className="an-table-wrap">
                        <table className="an-table">
                            <thead>
                                <tr>
                                    <th>Працівник</th>
                                    <th>Місто</th>
                                    <th>Посада</th>
                                    <th>Проходжень</th>
                                    <th>Не пройдено</th>
                                    <th>Середній бал</th>
                                    <th>Проблемні типи</th>
                                </tr>
                            </thead>
                            <tbody>
                                {riskRows.map((row, index) => (
                                    <tr key={`${row.city}-${row.studentLastName}-${row.studentName}-${index}`}>
                                        <td>{`${row.studentLastName || ''} ${row.studentName || ''}`.trim() || '—'}</td>
                                        <td>{row.city || '—'}</td>
                                        <td>{row.position || '—'}</td>
                                        <td>{row.count}</td>
                                        <td>{row.failed}</td>
                                        <td>{row.avgPercentage}%</td>
                                        <td>
                                            {(row.failedTypes || []).map((type) => (
                                                <span key={type} className={`type-badge type-${type}`}>
                                                    {TYPE_LABELS[type] || type}
                                                </span>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="an-empty">
                        <span className="an-empty-icon">✓</span>
                        <span>Критичних ризиків за цей період немає</span>
                    </div>
                )}
            </div>

            {/* Нижній ряд */}
            <div className="analytics-bottom">
                {/* Топ тестів */}
                <div className="an-chart-card">
                    <p className="an-chart-title">Трафік тестів</p>
                    {tests.length > 0 ? (
                        <div className="an-table-wrap">
                            <table className="an-table">
                                <thead>
                                    <tr>
                                        <th>Тип</th>
                                        <th>Назва тесту</th>
                                        <th>Місто</th>
                                        <th>Відвід.</th>
                                        <th>Конверсія</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tests.slice(0, 10).map((t, i) => (
                                        <tr key={i}>
                                            <td>
                                                <span className={`type-badge type-${t.testType}`}>
                                                    {TYPE_LABELS[t.testType] || t.testType}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: 500 }}>{t.title || '—'}</td>
                                            <td>{t.city || '—'}</td>
                                            <td>{t.views}</td>
                                            <td>
                                                <div className="conv-bar-wrap">
                                                    <div className="conv-bar-bg">
                                                        <div
                                                            className="conv-bar-fill"
                                                            style={{ width: `${t.conversion}%` }}
                                                        />
                                                    </div>
                                                    <span className="conv-pct">{t.conversion}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="an-empty">
                            <span className="an-empty-icon">📭</span>
                            <span>Тести ще не відвідувались</span>
                        </div>
                    )}
                </div>

                {/* Географія */}
                <div className="an-chart-card">
                    <p className="an-chart-title">Географія гравців</p>
                    {cityPieData.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={160}>
                                <PieChart>
                                    <Pie
                                        data={cityPieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={45}
                                        outerRadius={70}
                                        paddingAngle={3}
                                        dataKey="value"
                                    >
                                        {cityPieData.map((_, idx) => (
                                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            background: theme === 'dark' ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                            border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                                            borderRadius: 12,
                                            color: theme === 'dark' ? '#f8fafc' : '#0f172a',
                                            backdropFilter: 'blur(10px)',
                                            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <ul className="an-city-list">
                                {cityPieData.map((c, i) => (
                                    <li key={i} className="an-city-item">
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                                        <span className="an-city-name">{c.name}</span>
                                        <div className="conv-bar-bg" style={{ flex: 1 }}>
                                            <div
                                                className="conv-bar-fill"
                                                style={{
                                                    width: `${Math.round((c.value / cityPieData[0].value) * 100)}%`,
                                                    background: PIE_COLORS[i % PIE_COLORS.length]
                                                }}
                                            />
                                        </div>
                                        <span className="an-city-count">{c.value}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    ) : (
                        <div className="an-empty">
                            <span className="an-empty-icon">🗺️</span>
                            <span>Немає географічних даних</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Analytics;
